import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import AppStoreButton from "@/components/base/AppStoreButton";

const productLinks = [
  { label: "商務會議", to: "/business-meeting" },
  { label: "離線翻譯", to: "/offline-translation" },
  { label: "商務差旅", to: "/business-travel" },
];

const navLinks = [
  { label: "隱私與 On-Device", to: "/privacy" },
  { label: "使用方式", to: "/how-it-works" },
  { label: "方案價格", to: "/pricing" },
  { label: "知識內容", to: "/blog" },
  { label: "支援", to: "/support" },
];

const mobileMainLinks = [
  { label: "隱私與 On-Device", to: "/privacy" },
  { label: "使用方式", to: "/how-it-works" },
  { label: "方案價格", to: "/pricing" },
  { label: "知識內容", to: "/blog" },
  { label: "支援中心", to: "/support" },
  { label: "關於 keepvox", to: "/about" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [mobileProductOpen, setMobileProductOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setMobileProductOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setMobileProductOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => {
      if (mq.matches) {
        setMenuOpen(false);
        setMobileProductOpen(false);
      }
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const closeMenu = () => {
    setMenuOpen(false);
    setMobileProductOpen(false);
  };

  const isProductActive = productLinks.some((l) => location.pathname === l.to);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          scrolled || menuOpen
            ? "border-b border-background-800 bg-background-950/85 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-6 md:h-20 md:px-8">
          <Link
            to="/"
            className="font-heading text-xl font-bold tracking-tight text-foreground-50 md:text-2xl"
            aria-label="keepvox 首頁"
          >
            keepvox<span className="text-primary-400">.</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex" aria-label="主要導覽">
            <div
              className="relative"
              onMouseEnter={() => setProductOpen(true)}
              onMouseLeave={() => setProductOpen(false)}
            >
              <button
                type="button"
                onClick={() => setProductOpen((v) => !v)}
                className="flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-[1.0625rem] font-medium text-foreground-200 transition-colors hover:text-foreground-50"
              >
                產品功能
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform ${
                    productOpen ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                ></i>
              </button>

              {productOpen && (
                <div className="absolute left-0 top-full w-56 pt-2">
                  <div className="overflow-hidden rounded-lg border border-background-800 bg-background-900 py-1 shadow-lg">
                    {productLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className="block whitespace-nowrap px-4 py-2.5 text-sm text-foreground-200 transition-colors hover:bg-background-800 hover:text-foreground-50"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2 text-[1.0625rem] font-medium transition-colors hover:text-foreground-50 ${
                    isActive ? "text-primary-400" : "text-foreground-200"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <AppStoreButton />
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-11 w-11 items-center justify-center rounded-md text-foreground-100 transition-colors hover:bg-background-800 lg:hidden"
            aria-label={menuOpen ? "關閉選單" : "開啟選單"}
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
          >
            <i
              className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}
              aria-hidden="true"
            ></i>
          </button>
        </div>
      </header>

      {/* Mobile menu panel — rendered outside <header> so backdrop-filter never traps its fixed positioning */}
      {menuOpen && (
        <div
          id="mobile-navigation"
          className="mobile-menu-panel fixed inset-x-0 top-16 bottom-0 z-40 overflow-y-auto bg-background-950 lg:hidden md:top-20"
        >
          <nav
            className="flex flex-col px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]"
            aria-label="手機版導覽"
          >
            <NavLink
              to="/"
              onClick={closeMenu}
              className={({ isActive }) =>
                `whitespace-nowrap border-b border-background-800 py-3.5 text-[1.125rem] font-medium transition-colors hover:text-foreground-50 ${
                  isActive ? "text-primary-400" : "text-foreground-200"
                }`
              }
            >
              首頁
            </NavLink>

            <button
              type="button"
              onClick={() => setMobileProductOpen((v) => !v)}
              className={`flex w-full items-center justify-between whitespace-nowrap border-b border-background-800 py-3.5 text-[1.125rem] font-medium transition-colors hover:text-foreground-50 ${
                isProductActive ? "text-primary-400" : "text-foreground-200"
              }`}
              aria-expanded={mobileProductOpen}
              aria-controls="mobile-product-submenu"
            >
              產品功能
              <i
                className={`ri-arrow-down-s-line text-xl transition-transform duration-200 ${
                  mobileProductOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              ></i>
            </button>

            {mobileProductOpen && (
              <div id="mobile-product-submenu" className="border-b border-background-800">
                {productLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `flex items-center whitespace-nowrap py-3.5 pl-6 text-[1.125rem] font-medium transition-colors hover:text-foreground-50 ${
                        isActive ? "text-primary-400" : "text-foreground-300"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            )}

            {mobileMainLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={closeMenu}
                className={({ isActive }) =>
                  `whitespace-nowrap border-b border-background-800 py-3.5 text-[1.125rem] font-medium transition-colors hover:text-foreground-50 ${
                    isActive ? "text-primary-400" : "text-foreground-200"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="px-6 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
            <AppStoreButton className="w-full" size="lg" />
          </div>
        </div>
      )}
    </>
  );
}